import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  BarChart,
  Email,
  Chat,
  Facebook,
  Instagram,
  WhatsApp,
  Api,
  Sms,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface ChannelAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface ChannelData {
  conversations_by_channel: Record<string, number>;
  conversations_by_status: Record<string, number>;
  daily_breakdown: Array<{
    date: string;
    conversations: number;
    resolved: number;
  }>;
  total_conversations: number;
  resolved_conversations: number;
  avg_first_response_time: number;
}

const getChannelIcon = (channelType: string) => {
  switch (channelType.toLowerCase()) {
    case 'email': return <Email />;
    case 'whatsapp': return <WhatsApp />;
    case 'facebook': return <Facebook />;
    case 'instagram': return <Instagram />;
    case 'api': return <Api />;
    case 'sms': return <Sms />;
    case 'website': return <Chat />;
    default: return <Chat />;
  }
};

const getChannelColor = (channelType: string) => {
  switch (channelType.toLowerCase()) {
    case 'email': return '#EA4335';
    case 'whatsapp': return '#25D366';
    case 'facebook': return '#1877F2';
    case 'instagram': return '#E4405F';
    case 'api': return '#6366F1';
    case 'sms': return '#0088CC';
    case 'website': return '#1f93ff';
    default: return '#6B7280';
  }
};

const ChannelMetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
  icon: React.ReactNode;
  trend?: number;
}> = ({ title, value, subtitle, color, icon, trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color, mb: 1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend > 0 ? (
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
              ) : trend < 0 ? (
                <TrendingDown sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
              ) : null}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary' 
                }}
              >
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{ bgcolor: color, opacity: 0.8 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export const ChannelAnalytics: React.FC<ChannelAnalyticsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelData, setChannelData] = useState<ChannelData | null>(null);

  useEffect(() => {
    fetchChannelAnalytics();
  }, [dateRange]);

  const fetchChannelAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getConversationReport(params);
      setChannelData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch channel analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!channelData || Object.keys(channelData.conversations_by_channel).length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BarChart sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Channel Analytics
          </Typography>
        </Box>
        <Alert severity="info">
          No channel analytics data available for the selected period.
        </Alert>
      </Box>
    );
  }

  // Calculate channel performance metrics
  const channelPerformance = Object.entries(channelData.conversations_by_channel).map(([channel, conversations]) => {
    // Calculate resolution rate (simplified assumption)
    const resolutionRate = channelData.total_conversations > 0 
      ? (conversations / channelData.total_conversations) * (channelData.resolved_conversations / channelData.total_conversations) * 100
      : 0;
    
    const percentage = channelData.total_conversations > 0 
      ? (conversations / channelData.total_conversations) * 100 
      : 0;

    return {
      channel,
      conversations,
      percentage,
      resolutionRate,
      color: getChannelColor(channel),
      icon: getChannelIcon(channel)
    };
  }).sort((a, b) => b.conversations - a.conversations);

  const topChannel = channelPerformance[0];

  // Prepare data for export
  const exportData = channelPerformance || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BarChart sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Channel Analytics
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="channel_analytics"
          metadata={{ dateRange }}
        />
      </Box>

      {/* Channel Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ChannelMetricCard
            title="Total Channels"
            value={channelPerformance.length}
            subtitle="Active channels"
            color="#1f93ff"
            icon={<BarChart />}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ChannelMetricCard
            title="Top Channel"
            value={topChannel?.channel.toUpperCase() || 'N/A'}
            subtitle={`${topChannel?.conversations || 0} conversations`}
            color={topChannel?.color || '#6B7280'}
            icon={topChannel?.icon || <Chat />}
            trend={15.3}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ChannelMetricCard
            title="Total Conversations"
            value={channelData.total_conversations}
            subtitle="Across all channels"
            color="#10B981"
            icon={<Chat />}
            trend={8.2}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ChannelMetricCard
            title="Resolution Rate"
            value={`${((channelData.resolved_conversations / channelData.total_conversations) * 100).toFixed(0)}%`}
            subtitle="Overall performance"
            color="#F59E0B"
            icon={<CheckCircle />}
            trend={5.7}
          />
        </Grid>
      </Grid>

      {/* Channel Performance Comparison */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Channel Performance Comparison
              </Typography>
              <Box sx={{ mt: 2 }}>
                {channelPerformance.map((channel, index) => (
                  <Box key={channel.channel} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: channel.color, mr: 2, width: 32, height: 32 }}>
                        {channel.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                            {channel.channel}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {channel.conversations} conversations ({channel.percentage.toFixed(1)}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={channel.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              bgcolor: channel.color
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Channel Distribution
              </Typography>
              <Box sx={{ mt: 2 }}>
                {channelPerformance.map((channel, index) => (
                  <Box key={channel.channel} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: channel.color,
                        mr: 2
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {channel.channel}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {channel.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Channel Metrics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Channel Metrics
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Channel</TableCell>
                  <TableCell align="right">Conversations</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                  <TableCell align="right">Est. Resolution Rate</TableCell>
                  <TableCell align="right">Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channelPerformance.map((channel, index) => (
                  <TableRow key={channel.channel}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: channel.color, mr: 2, width: 32, height: 32 }}>
                          {channel.icon}
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                          {channel.channel}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {channel.conversations.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {channel.percentage.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Box sx={{ width: 60, mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(channel.resolutionRate, 100)} 
                            color={channel.resolutionRate > 80 ? 'success' : channel.resolutionRate > 60 ? 'warning' : 'error'}
                          />
                        </Box>
                        <Typography variant="body2">
                          {channel.resolutionRate.toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={index === 0 ? 'Best' : index === 1 ? 'Good' : index === 2 ? 'Average' : 'Below Avg'}
                        color={index === 0 ? 'success' : index === 1 ? 'primary' : index === 2 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};